import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import type { ToolView } from "../store/selectors";
import { useDebug } from "./debug";
import { execLines, formatDuration, type CellSummary, type ExecOutput, type Step } from "./steps";
import { ToolLine } from "./ToolLine";

const OUTPUT =
  "whitespace-pre-wrap rounded-md bg-muted px-2 py-1 font-mono text-[11px] leading-4 text-foreground/70 [overflow-wrap:anywhere]";

/** What a command printed: the last lines of its output, or of its errors (`execLines`). */
function ExecTail({ output }: { output: ExecOutput }) {
  const lines = execLines(output);
  if (!lines.length) return null;
  return (
    <>
      <pre data-agent-step-output className={OUTPUT}>
        {lines.join("\n")}
      </pre>
      {output.truncated && <p className="text-[11px] text-muted-foreground">Earlier output cut</p>}
    </>
  );
}

/** A code cell's summary, unless the line already is it. Its approvals show as their own cards
 * after it. */
const cellFact = (cell: CellSummary, line: string) =>
  cell.summary && cell.summary !== line ? cell.summary : null;

/** What a code cell did and how long it took; with nothing else to say, the time is on the line. */
function CellFacts({ summary, durationMs }: { summary: string; durationMs: number }) {
  return (
    <div className="flex flex-col gap-0.5 text-[11px] text-muted-foreground">
      <p className="break-words text-foreground">{summary}</p>
      {durationMs > 0 && <p>{formatDuration(durationMs)}</p>}
    </div>
  );
}

/**
 * A sandbox call (bash, repl, a code cell) as its line with a card under it: what it printed,
 * or what the cell did. Its code shows only in debug (`useDebug`), folded. All of it is text from
 * the sandbox, rendered inert. `children` go on the line, after it (the dev inspector's icon).
 */
export function StepCard({
  line,
  state,
  step,
  children,
}: {
  line: string;
  state: ToolView["state"];
  step: Step;
  children?: ReactNode;
}) {
  const debug = useDebug();
  const summary = step.kind === "cell" ? cellFact(step.cell, line) : null;
  const lineDuration =
    step.kind === "cell" && !summary && step.cell.durationMs > 0
      ? formatDuration(step.cell.durationMs)
      : undefined;
  return (
    <div data-agent-step={step.kind} className="flex min-w-0 flex-col gap-1 py-0.5">
      <ToolLine line={line} state={state} detail={lineDuration}>
        {children}
      </ToolLine>
      <div className="ml-1.5 flex min-w-0 flex-col gap-1 border-l pl-3 empty:hidden">
        {step.kind === "exec"
          ? step.output && <ExecTail output={step.output} />
          : summary && <CellFacts summary={summary} durationMs={step.cell.durationMs} />}
        {debug && step.code && (
          // Open, its block keeps a card that follows (a cell's approval) off it.
          <details className="text-[11px] text-muted-foreground open:pb-1.5">
            <summary className="w-fit cursor-pointer rounded hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              Code
            </summary>
            <pre className={cn("mt-1", OUTPUT)}>{step.code}</pre>
          </details>
        )}
      </div>
    </div>
  );
}
